import { Link, useLocation } from "react-router-dom";
import { LoginSocialGoogle } from "reactjs-social-login";
import React, { useContext, useEffect, useState } from "react";
import { StoreContext } from "../../core/context/StoreContext";
import { useTranslation } from "react-i18next";
import { postLogin, setDefaultHeader } from "../../core/api/Api";
import {
  decryptString,
  deleteCookie,
  encryptString,
  getCookie,
  setCookie,
  showToast,
  buildLocalizedPath,
} from "../../core/common-service/CommonService";

import "./LoginBox.css";
import useLangNavigate from "../common/useLangNavigate";
import { LoginSignUpEvent } from "../../core/utils/loginSignupEvent";

export function LoginBox() {
  const { setIsLoggedIn, setUser } = useContext(StoreContext);

  const navigate = useLangNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const onEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const onPasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const onShowHidePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const onRememberMeChange = () => {
    setRememberMe((prop) => !prop);
  };

  const handleLogin = (body) => {
    postLogin(body)
      .then((res) => {
        setLoading(false);
        const status_code = res?.status;
        const response = res?.data?.success;
        const is_new_user = response?.is_new_user;
        if (status_code === 200) {
          const token = response?.token;
          const user = response?.user;
          if (user?.is_verified) {
            const is_google_login = body?.access_token ? true : false;
            localStorage.setItem(
              "isGoogleLogin",
              is_google_login ? "true" : "false",
            );
            LoginSignUpEvent({
              user: user,
              is_new: is_new_user,
              is_google: is_google_login,
            });
            const service_session_id = response?.service_session_id ?? null;
            setDefaultHeader("Bearer " + token);
            setIsLoggedIn(true);
            setUser(user);
            setCookie("token", token);
            setTimeout(async () => {
              if (rememberMe) {
                const credentials = await encryptString(
                  JSON.stringify({ email, password }),
                );
                setCookie("credentials", JSON.stringify(credentials));
              } else {
                deleteCookie("credentials");
                sessionStorage.setItem("token", token);
              }

              if (is_new_user) {
                navigate("/thankyou/register", {
                  state: { email },
                });
                return;
              }

              if (service_session_id) {
                navigate("/questions/" + service_session_id);
                localStorage.removeItem("trial_session");
              } else {
                const from = location.state?.from || "/my-books";
                navigate(from, "", { state: location.state });
              }
            }, 300);
          } else {
            showToast({ type: "error", message: t("error.user_not_allowed") });
          }
        }
      })
      .catch((e) => {
        setLoading(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;

    if (!email.trim()) {
      showToast({ type: "error", message: t("error.email_required") });
      return;
    }

    if (!email.includes("@")) {
      showToast({ type: "error", message: t("error.invalid_email") });
      return;
    }

    if (!password.trim()) {
      showToast({ type: "error", message: t("error.password_required") });
      return;
    }

    const email_input = document.getElementById("email").value ?? email;
    const password_input =
      document.getElementById("password").value ?? password;
    setLoading(true);
    const trial_session = localStorage.getItem("trial_session");
    handleLogin({ email: email_input, password: password_input, trial_session });
  };

  const onResolveGoogleLogin = ({ provider, data }) => {
    setLoading(true);
    const trial_session = localStorage.getItem("trial_session");
    handleLogin({
      access_token: data.access_token,
      trial_session,
      given_name: data.given_name,
      family_name: data.family_name,
    });
  };

  const getCredentials = async () => {
    const credentialsCookie = getCookie("credentials");
    if (credentialsCookie) {
      const credentialsStr = await decryptString(JSON.parse(credentialsCookie));
      const credentials = JSON.parse(credentialsStr);
      setEmail(credentials?.email);
      setPassword(credentials?.password);
    }
  };

  useEffect(() => {
    try {
      getCredentials();
    } catch (e) {
      showToast({ type: "error", message: t("error.unknown_error") });
    }

    return () => {};
  }, []);

  return (
    <div className="modern-login-wrapper">
      <div className="modern-login-card">
        {/* Header */}
        <div className="modern-login-header">
          <h2 className="modern-login-title">{t("login_to_your_account")}</h2>
        </div>

        {/* Google Login Button */}
        <LoginSocialGoogle
          isOnlyGetToken
          scope={"https://www.googleapis.com/auth/userinfo.email"}
          client_id={process.env.REACT_APP_GG_APP_ID || ""}
          onResolve={onResolveGoogleLogin}
          onReject={(err) => {
            showToast({ type: "error", message: t("error.unknown_error") });
          }}
        >
          <button
            type="button"
            className="modern-google-btn"
            disabled={loading}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 23 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21.1258 11.2139C21.1258 10.4225 21.0603 9.84497 20.9185 9.24609H11.6973V12.818H17.1099C17.0008 13.7057 16.4115 15.0425 15.102 15.9408L15.0836 16.0603L17.9992 18.2738L18.2012 18.2936C20.0563 16.6145 21.1258 14.1441 21.1258 11.2139"
                fill="#4285F4"
              />
              <path
                d="M11.6976 20.6248C14.3494 20.6248 16.5755 19.7692 18.2016 18.2934L15.1024 15.9405C14.273 16.5073 13.1599 16.903 11.6976 16.903C9.10043 16.903 6.89609 15.224 6.11031 12.9033L5.99513 12.9129L2.96347 15.2122L2.92383 15.3202C4.53888 18.4644 7.85634 20.6248 11.6976 20.6248Z"
                fill="#34A853"
              />
              <path
                d="M6.10908 12.9034C5.90174 12.3045 5.78175 11.6628 5.78175 10.9997C5.78175 10.3366 5.90174 9.695 6.09817 9.09612L6.09268 8.96857L3.02303 6.63232L2.92259 6.67914C2.25695 7.98388 1.875 9.44905 1.875 10.9997C1.875 12.5504 2.25695 14.0155 2.92259 15.3203L6.10908 12.9034"
                fill="#FBBC05"
              />
              <path
                d="M11.6977 5.09664C13.5419 5.09664 14.7859 5.87733 15.4953 6.52974L18.2671 3.8775C16.5648 2.32681 14.3494 1.375 11.6977 1.375C7.85637 1.375 4.53889 3.53526 2.92383 6.6794L6.09942 9.09638C6.89612 6.77569 9.10047 5.09664 11.6977 5.09664"
                fill="#EB4335"
              />
            </svg>
            <span>{t("sign_in_with_google")}</span>
          </button>
        </LoginSocialGoogle>

        {/* Form */}
        <form onSubmit={handleSubmit} className="modern-login-form">
          {/* Email Field */}
          <div className="modern-form-group">
            <label className="modern-form-label">
              {t("your_email_address")}
            </label>
            <input
              className="modern-form-input"
              id="email"
              name="email"
              type="email"
              placeholder={t("your_email_address")}
              value={email}
              onChange={onEmailChange}
              disabled={loading}
            />
          </div>

          {/* Password Field */}
          <div className="modern-form-group">
            <label className="modern-form-label">
              {t("password") || "Password"}
            </label>
            <div className="modern-password-wrapper">
              <input
                className="modern-form-input"
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder={t("enter_your_password")}
                value={password}
                onChange={onPasswordChange}
                disabled={loading}
              />
              <button
                type="button"
                className="modern-password-toggle"
                onClick={onShowHidePassword}
              >
                <i
                  className={
                    "fa-regular fa-eye" + (showPassword ? "" : "-slash")
                  }
                ></i>
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="modern-form-row">
            <div
              className="modern-checkbox-wrapper"
              onClick={onRememberMeChange}
            >
              <input
                type="checkbox"
                className="modern-checkbox"
                checked={rememberMe}
                readOnly
              />
              <label className="modern-checkbox-label">
                {t("remember_me")}
              </label>
            </div>
            <Link
              to={buildLocalizedPath("/forgot-password")}
              className="modern-forgot-link"
            >
              {t("forgot_password") || "Forgot Password"}
            </Link>
          </div>

          {/* Login Button */}
          <button type="submit" disabled={loading} className="modern-login-btn">
            {loading
              ? t("logging_in") || "Logging in..."
              : t("login") || "Log In"}
          </button>
        </form>

        {/* Sign Up Link */}
        <div className="modern-signup-link">
          <span>{t("no_account") || "No account?"} </span>
          <Link to={buildLocalizedPath("/register")} className="modern-link">
            {t("signup")}
          </Link>
        </div>
      </div>

      {loading && <div className="modern-loader"></div>}
    </div>
  );
}
